import { startTransition, useEffect, useState } from "react";
import { fetchWithSupabaseAuth } from "../lib/supabaseClient.js";
import { appendDispatchDateToUrl } from "../utils/dispatchDateUrls.js";
import {
  DISPATCH_REASSIGNMENT_MODE_COMPLETE,
  appendDispatchReassignmentModeSearchParam,
} from "../utils/dispatchReassignmentMode.js";

export async function fetchReassignmentCandidates({
  dispatchDate = "",
  dispatchPeriod = "",
  includeRouteImpact = false,
  rebuildMode = DISPATCH_REASSIGNMENT_MODE_COMPLETE,
  resolveLocalRebuild = true,
  signal,
  stepId = null,
  stepType = null,
}) {
  if (!stepId || !stepType) {
    return { contexts: [], step: null };
  }

  const searchParams = new URLSearchParams();
  appendDispatchReassignmentModeSearchParam(searchParams, rebuildMode);
  if (includeRouteImpact) {
    searchParams.set("include_route_impact", "true");
  }
  if (!resolveLocalRebuild) {
    searchParams.set("resolve_local_rebuild", "false");
  }

  const requestUrl = appendDispatchDateToUrl(
    `/api/planning/reassignment-candidates/${encodeURIComponent(stepType)}/${encodeURIComponent(stepId)}${searchParams.size ? `?${searchParams.toString()}` : ""}`,
    dispatchDate || undefined,
    dispatchPeriod || undefined,
  );
  const response = await fetchWithSupabaseAuth(requestUrl, {
    headers: {
      Accept: "application/json",
    },
    signal,
  });
  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(payload?.error || "Impossible de charger les camions compatibles.");
  }

  return payload;
}

export async function fetchSuspendedDeliveryReactivationCandidates({
  deliveryId = null,
  dispatchDate = "",
  dispatchPeriod = "",
  includeRouteImpact = false,
  rebuildMode = DISPATCH_REASSIGNMENT_MODE_COMPLETE,
  signal,
}) {
  if (!deliveryId) {
    return { contexts: [], step: null };
  }

  const searchParams = new URLSearchParams();
  appendDispatchReassignmentModeSearchParam(searchParams, rebuildMode);
  if (includeRouteImpact) {
    searchParams.set("include_route_impact", "true");
  }

  const requestUrl = appendDispatchDateToUrl(
    `/api/planning/suspended-deliveries/${encodeURIComponent(deliveryId)}/reassignment-candidates${searchParams.size ? `?${searchParams.toString()}` : ""}`,
    dispatchDate || undefined,
    dispatchPeriod || undefined,
  );
  const response = await fetchWithSupabaseAuth(requestUrl, {
    headers: {
      Accept: "application/json",
    },
    signal,
  });
  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(payload?.error || "Impossible de charger les camions compatibles.");
  }

  return payload;
}

export default function useReassignmentCandidates({
  contexts = [],
  dispatchDate = "",
  dispatchPeriod = "",
  error = null,
  isOpen = true,
  loading = false,
  rebuildMode = DISPATCH_REASSIGNMENT_MODE_COMPLETE,
  stepId = null,
  stepType = null,
}) {
  const [stepData, setStepData] = useState(null);
  const [remoteContexts, setRemoteContexts] = useState(Array.isArray(contexts) ? contexts : []);
  const [remoteError, setRemoteError] = useState(error);
  const [isLoading, setIsLoading] = useState(Boolean(loading));
  const [showRouteImpact, setShowRouteImpact] = useState(false);
  const [isRouteImpactPending, setIsRouteImpactPending] = useState(false);
  const includeRouteImpact = showRouteImpact || isRouteImpactPending;

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setShowRouteImpact(false);
    setIsRouteImpactPending(false);
  }, [dispatchDate, dispatchPeriod, isOpen, rebuildMode, stepId, stepType]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setStepData(null);
    setRemoteContexts([]);
    setRemoteError(null);
  }, [dispatchDate, dispatchPeriod, isOpen, rebuildMode, stepId, stepType]);

  useEffect(() => {
    if (!isOpen || !stepId || !stepType) {
      return undefined;
    }

    const controller = new AbortController();

    setIsLoading(true);
    setRemoteError(null);

    fetchReassignmentCandidates({
      dispatchDate,
      dispatchPeriod,
      includeRouteImpact,
      rebuildMode,
      signal: controller.signal,
      stepId,
      stepType,
    }).then((payload) => {
        startTransition(() => {
          setStepData(payload?.step ?? null);
          setRemoteContexts(Array.isArray(payload?.contexts) ? payload.contexts : []);
          setShowRouteImpact(includeRouteImpact);
        });
      })
      .catch((fetchError) => {
        if (controller.signal.aborted) {
          return;
        }
        setRemoteError(fetchError instanceof Error ? fetchError.message : "Erreur inconnue.");
        setRemoteContexts([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoading(false);
          setIsRouteImpactPending(false);
        }
      });

    return () => {
      controller.abort();
    };
  }, [dispatchDate, dispatchPeriod, includeRouteImpact, isOpen, rebuildMode, stepId, stepType]);

  const hasCurrentStepData = stepData?.step_id === stepId && stepData?.step_type === stepType;
  const resolvedContexts = hasCurrentStepData && Array.isArray(remoteContexts) ? remoteContexts : [];
  const resolvedStep = hasCurrentStepData ? stepData : null;
  const resolvedStepType = resolvedStep?.step_type ?? stepType ?? null;
  const stepLabel = resolvedStepType === "loading"
    ? "Chargement"
    : resolvedStepType === "delivery"
      ? "Déchargement"
      : "Réaffectation";
  const compatibleTruckCount = resolvedContexts.reduce((total, context) => {
    const compatibilities = Array.isArray(context?.compatibilities)
      ? context.compatibilities
      : [];
    return total + compatibilities.length;
  }, 0);
  const hasDisplayedCompatibilities = compatibleTruckCount > 0;
  const shouldRenderCompatibilities = hasDisplayedCompatibilities;
  const shouldShowLoadingState = includeRouteImpact && isLoading && !hasDisplayedCompatibilities;
  const impactCalculationStatus = isRouteImpactPending && !showRouteImpact
    ? {
      message: "Nous comparons les tournées source, cible et flotte pour les camions affichés.",
      title: "Calcul des impacts en cours",
      type: "loading",
    }
    : null;
  const modeToggleLabel = isRouteImpactPending
    ? "Calcul des impacts..."
    : showRouteImpact
    ? "Revenir à la vue rapide"
    : "Activer les calculs";
  const informationText = showRouteImpact
    ? "La ligne de référence reprend la tournée actuelle. Les autres lignes montrent l'impact global source, cible et flotte après transfert."
    : "Seuls les camions compatibles pour un déplacement sont affichés, avec leurs indicateurs opérationnels sans recalcul global.";
  const popoverWidthClass = showRouteImpact
    ? "w-[min(70rem,calc(100vw-0.75rem))] max-w-280"
    : "w-[min(56rem,calc(100vw-0.75rem))] max-w-224";

  return {
    compatibleTruckCount,
    hasDisplayedCompatibilities,
    impactCalculationStatus,
    informationText,
    isLoading,
    isRouteImpactPending,
    modeToggleLabel,
    modeToggleDisabled: isLoading || isRouteImpactPending,
    popoverWidthClass,
    remoteError,
    resolvedContexts,
    resolvedStep,
    shouldShowLoadingState,
    shouldRenderCompatibilities,
    showRouteImpact,
    stepLabel,
    toggleRouteImpact() {
      if (isLoading) {
        return;
      }

      if (showRouteImpact) {
        setShowRouteImpact(false);
        return;
      }

      setIsRouteImpactPending(true);
    },
  };
}
